
import { Formik, FieldArray } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import FreelancerLayout from "../../layouts/FreelancerLayout";
import TagInput from "../../components/form/TagInput";
import ErrorMessage from "../../components/form/ErrorMessage";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { findAJobPaths } from "../../routes/find-a-job";
import { getProfile, setProfile } from "../../utils/pip";
import Loader from "../../components/loader";
import PdfOrImageUploader from "../../components/pdfImage uploader";
import {
  fetchfreelancerProfile,
  freelancerUpdateProfileStep,
} from "../../redux/features/find-a-job/actions/authAction";
import { freelancerEditProfileSchema } from "../../utils/schema";
import { message } from "antd";

const defaultProfileImage = "/assets/img/user_profile.png";

const EditProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { subLoading } = useSelector((state) => state.freelancer.auth);
  const { isLoading, freelancerProfileData } = useSelector((state) => state.freelancer.auth);
  const hasServerProfile =
    freelancerProfileData && Object.keys(freelancerProfileData).length > 0;
  const profileData = hasServerProfile
    ? freelancerProfileData
    : getProfile("freelancer") || {};
  const openTalent = profileData?.open_talent || {};

  const parseArray = (value, fallback = []) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return fallback;
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed : fallback;
      } catch {
        return trimmed
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }
    return fallback;
  };

  const normalizeList = (value) =>
    (parseArray(value, []) || [])
      .map((item) => String(item || "").trim())
      .filter(Boolean);

  const normalizeAvailabilityList = (value) => {
    const availabilityMap = {
      remote_freelance: "Remote Freelance",
      full_time_freelance: "Full-time Freelance",
      part_time_freelance: "Part-time Freelance",
      hybrid_freelance: "Hybrid Freelance",
      on_site_freelance: "On-Site Freelance",
      onsite_freelance: "On-Site Freelance",
      project_based: "Project Based",
      project_based_freelance: "Project Based",
    };

    return normalizeList(value).map((item) => {
      const key = String(item || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");
      if (availabilityMap[key]) return availabilityMap[key];
      if (availabilityMap[item]) return availabilityMap[item];
      const titleCased = key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (match) => match.toUpperCase());
      return titleCased.replace("On Site", "On-Site").replace("Part Time", "Part-time").replace("Full Time", "Full-time");
    });
  };

  const hasOption = (list, option) => {
    const target = String(option || "").trim().toLowerCase();
    return (list || []).some(
      (item) => String(item || "").trim().toLowerCase() === target
    );
  };

  const normalizeDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toISOString().slice(0, 10);
  };

  const normalizeExperiences = (value) => {
    const items = parseArray(value, []);
    if (!items.length) {
      return [
        {
          job_title: "",
          company_name: "",
          job_description: "",
          location: "",
          experience_images: "",
          start_date: "",
          end_date: "",
          currently_working: false,
          link: "",
        },
      ];
    }
    return items.map((exp) => ({
      ...exp,
      location: exp?.location || "",
      experience_images:
        exp?.experience_images || exp?.image || exp?.experience_image || "",
      start_date: normalizeDate(exp.start_date),
      end_date: normalizeDate(exp.end_date),
    }));
  };

  const getExperienceImageValue = (exp) =>
    exp?.experience_images || exp?.image || exp?.experience_image || "";

  const normalizeExperienceForCompare = (exp) => ({
    experience_id: exp?.experience_id || exp?.id || exp?.experienceId || null,
    job_title: String(exp?.job_title || "").trim(),
    company_name: String(exp?.company_name || "").trim(),
    location: String(exp?.location || "").trim(),
    start_date: normalizeDate(exp?.start_date),
    end_date: normalizeDate(exp?.end_date),
    currently_working: !!exp?.currently_working,
    job_description: String(exp?.job_description || "").trim(),
    link: String(exp?.link || "").trim(),
    image: String(getExperienceImageValue(exp) || ""),
  });

  const normalizePhoneValue = (value) => {
    if (!value) return "";
    const trimmed = String(value).trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("+")) return trimmed;
    const digits = trimmed.replace(/\D/g, "");
    if (!digits) return "";
    return `+${digits}`;
  };

  const normalizeWorkSamples = (workSamplesValue, projectsValue) => {
    const samples = parseArray(workSamplesValue, []);
    const projectItems = Array.isArray(projectsValue) ? projectsValue : [];
    const fromProjects = projectItems
      .filter((item) => item?.project_url)
      .map((item) => ({
        type: "existing",
        url: item.project_url,
        id: item.open_talent_project_id,
        name: item.project_url?.split("/").pop() || "Project",
      }));
    const fromSamples = samples.map((item) => {
      if (typeof item === "string") {
        return {
          type: "existing",
          url: item,
          name: item.split("/").pop() || item,
        };
      }
      return item;
    });
    return [...fromProjects, ...fromSamples];
  };

  const linkRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;
  const isValidLink = (value) => linkRegex.test(value);

  const roleOptions = [
    "UX Designer",
    "UI Designer",
    "UX Researcher",
    "Graphic Designer",
    "Product Designer",
    "Interaction Designer",
    "Frontend Developer",
    "Backend Developer",
    "Full Stack Developer",
    "Web Developer",
    "Mobile App Developer (iOS)",
    "Mobile App Developer (Android)",
    "Cross-Platform App Developer",
    "Software Engineer",
    "Application Developer",
    "API Developer",
    "DevOps Engineer",
    "Cloud Engineer",
    "Site Reliability Engineer (SRE)",
    "Infrastructure Engineer",
    "Data Analyst",
    "Data Scientist",
    "Machine Learning Engineer",
    "AI Engineer",
    "Data Engineer",
    "Cybersecurity Specialist",
    "Security Engineer",
    "Penetration Tester",
    "QA Engineer",
    "Test Automation Engineer",
    "Manual Tester",
    "Database Administrator",
    "Systems Administrator",
    "Network Engineer",
    "IT Support Specialist",
    "Blockchain Developer",
    "Game Developer",
    "Embedded Systems Engineer",
    "Technical Project Manager",
    "IT Project Manager",
    "Product Manager",
    "Scrum Master",
    "Agile Coach",
    "Business Analyst",
    "Technical Consultant",
    "Solutions Architect",
    "Enterprise Architect",
    "Other",
  ];

  const initialPrimaryRole = openTalent?.primary_role || "";
  const isOtherPrimaryRole =
    initialPrimaryRole && !roleOptions.includes(initialPrimaryRole);

  const initialValues = {
    full_name:
      profileData?.full_name ||
      `${profileData?.first_name || ""} ${profileData?.last_name || ""}`.trim(),
    profile_image: null,
    profile_image_preview: profileData?.profile_image || openTalent?.profile_image || "",
    first_name: profileData?.full_name || profileData?.first_name || "",
    last_name: profileData?.last_name || "",
    email: profileData?.email || "",
    phone_number: openTalent?.phone_number || profileData?.phone_number || "",
    country: openTalent?.country || "",
    city: openTalent?.city || "",
    linkedin_profile: openTalent?.linkedin_profile || "",
    portfolio_website: openTalent?.website_or_portfolio || openTalent?.portfolio_website || "",
    bio: openTalent?.bio || "",
    primary_role: isOtherPrimaryRole ? "Other" : initialPrimaryRole,
    other_primary_role: isOtherPrimaryRole ? initialPrimaryRole : "",
    years_of_experience: openTalent?.years_of_experience || "",
    skills: parseArray(openTalent?.skills || openTalent?.key_skills, []),
    tools: parseArray(openTalent?.tools_software_used || openTalent?.tools, []),
    availability: normalizeAvailabilityList(openTalent?.availability),
    hourly_rate: openTalent?.hourly_rate || "",
    preferred_project_type: normalizeList(openTalent?.preferred_project_type),
    experiences: normalizeExperiences(openTalent?.experiences || profileData?.experiences),
    portfolio_links: parseArray(openTalent?.sample_portfolio_link, [""]),
    work_samples: normalizeWorkSamples(openTalent?.work_samples, openTalent?.projects),
    cv: parseArray(openTalent?.cv || profileData?.cv, []),
    certificates: parseArray(openTalent?.certificates || profileData?.certificates, []),
    delete_project_ids: [],
    delete_certificate_ids: [],
    delete_experience_ids: [],
    join_reason: openTalent?.why_join_nuvio || "",
    interested_clients: openTalent?.clients_types || "",
    heard_about:
      openTalent?.how_hear_about_nuvio?.toLowerCase() || "",
  };

  const availabilityOptions = [
    { label: "Remote Freelance", value: "Remote Freelance" },
    { label: "Full-time Freelance", value: "Full-time Freelance" },
    { label: "Part-time Freelance", value: "Part-time Freelance" },
    { label: "Hybrid Freelance", value: "Hybrid Freelance" },
    { label: "On-Site Freelance", value: "On-Site Freelance" },
    { label: "Project-Based", value: "Project-Based" },
  ];

  const projectTypeOptions = [
    "Short-term projects",
    "Long-term contracts",
    "Startup collaborations",
    "Corporate clients",
  ];

  const toggleAvailability = (
    currentList,
    value,
    setFieldValue,
    setFieldError,
    setFieldTouched
  ) => {
    const current = new Set(currentList || []);
    if (current.has(value)) {
      current.delete(value);
    } else {
      if (current.size >= 3) {
        setFieldError("availability", undefined);
        message.error("You can select up to 3 availability options.");
        return;
      }
      current.add(value);
    }
    setFieldTouched("availability", true, false);
    setFieldValue("availability", Array.from(current));
  };

  useEffect(() => {
    dispatch(fetchfreelancerProfile()).then((res) => {
      const data = res?.payload?.data;
      if (data) {
        const merged = {
          ...data,
          full_name:
            data.full_name ||
            `${data.first_name || ""} ${data.last_name || ""}`.trim(),
          first_name: data.first_name || data.full_name || "",
          last_name: data.last_name || "",
        };
        setProfile("freelancer", merged);
      }
    });
  }, [dispatch]);

  const handleSave = (values) => {
    const full_name =
      values.full_name ||
      `${values.first_name} ${values.last_name}`.trim() ||
      profileData?.full_name ||
      profileData?.email?.split("@")[0] ||
      "";
    const resolvedPrimaryRole =
      values?.primary_role === "Other"
        ? values?.other_primary_role || ""
        : values?.primary_role || "";

    const resolveExperienceImage = (value) => {
      if (!value) return { file: null, value: "" };
      const item = Array.isArray(value) ? value[0] : value;
      if (!item) return { file: null, value: "" };
      if (item instanceof File) return { file: item, value: "" };
      if (item?.file instanceof File) return { file: item.file, value: "" };
      if (typeof item === "string") return { file: null, value: item };
      if (typeof item?.url === "string") return { file: null, value: item.url };
      if (typeof item?.preview === "string")
        return { file: null, value: item.preview };
      if (typeof item?.file === "string")
        return { file: null, value: item.file };
      return { file: null, value: "" };
    };

    const originalExperiences = normalizeExperiences(
      openTalent?.experiences || profileData?.experiences
    );
    const originalById = new Map(
      originalExperiences
        .map((exp) => normalizeExperienceForCompare(exp))
        .filter((exp) => exp.experience_id)
        .map((exp) => [String(exp.experience_id), exp])
    );

    const updatedExperiences = [];
    const updatedExperienceIds = [];
    const updatedImageFiles = [];

    const shouldMarkUpdated = (original, current) =>
      original.job_title !== current.job_title ||
      original.company_name !== current.company_name ||
      original.location !== current.location ||
      original.start_date !== current.start_date ||
      original.end_date !== current.end_date ||
      original.currently_working !== current.currently_working ||
      original.job_description !== current.job_description ||
      original.link !== current.link ||
      original.image !== current.image;

    const experienceImageFiles = [];
    const sanitizedExperiences = (values.experiences || [])
      .map((exp) => {
        const endDate =
          exp.currently_working || !exp.end_date ? null : exp.end_date;
        const resolvedImage = resolveExperienceImage(exp.experience_images);
        const expId = exp?.experience_id || exp?.id || exp?.experienceId || null;
        if (expId) {
          const normalizedCurrent = normalizeExperienceForCompare({
            ...exp,
            end_date: endDate,
            experience_images: resolvedImage.value || exp.experience_images,
          });
          const original = originalById.get(String(expId));
          const isImageUpdated = !!resolvedImage.file;
          const hasChanges = original
            ? shouldMarkUpdated(original, normalizedCurrent)
            : true;
          if (hasChanges || isImageUpdated) {
            updatedExperienceIds.push(expId);
            updatedExperiences.push({
              experience_id: expId,
              is_image_updated: isImageUpdated,
              job_title: normalizedCurrent.job_title || "",
              company_name: normalizedCurrent.company_name || "",
              location: normalizedCurrent.location || "",
              start_date: normalizedCurrent.start_date || null,
              end_date: endDate,
              currently_working: normalizedCurrent.currently_working || false,
              job_description: normalizedCurrent.job_description || "",
              link: normalizedCurrent.link || "",
            });
            if (isImageUpdated) {
              updatedImageFiles.push(resolvedImage.file);
            }
          }
          return null;
        }
        if (resolvedImage.file) {
          experienceImageFiles.push(resolvedImage.file);
        }
        return {
          ...exp,
          location: exp.location || "",
          experience_images: resolvedImage.value || "",
          end_date: endDate,
        };
      })
      .filter(Boolean);

    const formData = new FormData();
    if (values.profile_image instanceof File) {
      formData.append("profile_image", values.profile_image);
    }
    (values.work_samples || []).forEach((file) => {
      if (file instanceof File) {
        formData.append("projects", file);
      }
    });
    (values.cv || []).forEach((file) => {
      if (file instanceof File) {
        formData.append("cv", file);
      }
    });
    (values.certificates || []).forEach((file) => {
      if (file instanceof File) {
        formData.append("certificates", file);
      }
    });
    experienceImageFiles.forEach((file) => {
      formData.append("experience_images", file);
    });
    if (updatedExperienceIds.length) {
      formData.append(
        "updated_experience_ids",
        Array.from(new Set(updatedExperienceIds)).join(",")
      );
    }
    if (updatedExperiences.length) {
      formData.append("updated_experience", JSON.stringify(updatedExperiences));
    }
    updatedImageFiles.forEach((file) => {
      formData.append("updated_images", file);
    });

    formData.append("experiences", JSON.stringify(sanitizedExperiences));
    formData.append("full_name", full_name);
    formData.append("phone_number", values.phone_number || "");
    formData.append("country", values.country || "");
    formData.append("city", values.city || "");
    formData.append("linkedin_profile", values.linkedin_profile || "");
    formData.append("website_or_portfolio", values.portfolio_website || "");
    formData.append("bio", values.bio || "");
    formData.append("primary_role", resolvedPrimaryRole);
    formData.append("years_of_experience", values.years_of_experience || "");
    formData.append("skills", (values.skills || []).join(", "));
    formData.append("tools_software_used", (values.tools || []).join(", "));
    formData.append("availability", (values.availability || []).join(", "));
    formData.append("hourly_rate", values.hourly_rate || "");
    formData.append(
      "preferred_project_type",
      (values.preferred_project_type || []).join(", ")
    );
    formData.append(
      "sample_portfolio_link",
      values.portfolio_links || []
    );
    formData.append("why_join_nuvio", values.join_reason || "");
    formData.append("how_hear_about_nuvio", values.heard_about || "");
    formData.append("clients_types", values.interested_clients || "");
    const deleteProjectIds = Array.from(
      new Set((values.delete_project_ids || []).filter(Boolean))
    );
    if (deleteProjectIds.length) {
      formData.append("delete_project_ids", deleteProjectIds.join(","));
    }
    const deleteCertificateIds = Array.from(
      new Set((values.delete_certificate_ids || []).filter(Boolean))
    );
    if (deleteCertificateIds.length) {
      formData.append("delete_certificate_ids", deleteCertificateIds.join(","));
    }
    const deleteExperienceIds = Array.from(
      new Set((values.delete_experience_ids || []).filter(Boolean))
    );
    if (deleteExperienceIds.length) {
      formData.append("delete_experience_ids", deleteExperienceIds.join(","));
    }

    console.log(formData)

    const callback = (response) => {
      if (!response?.success) return;
      const updated = response?.data || {};
      setProfile("freelancer", updated);
      navigate(findAJobPaths.Profile);
    };

    dispatch(freelancerUpdateProfileStep({ payload: formData, callback }));
  };
  if (subLoading || isLoading) {
    return <Loader />;
  };
  return (
    <FreelancerLayout>
      <div className="ct_px_40 my-5">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="ct_fs_20_new ct_fw_600 ct_darkblue_text">Edit Profile</h4>
            <p className="mb-0 ct_grey_text">Update your freelancer profile information</p>
          </div>
          <button
            className="ct_yellow_btn ct_outline_grey ct_h_44"
            type="button"
            onClick={() => navigate(findAJobPaths.Profile)}
          >
            Back to Profile
          </button>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={freelancerEditProfileSchema}
          onSubmit={(values) => handleSave(values)}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
            setFieldError
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="ct_outline_bg ct_border_radius_10 ct_p_20">
                <div className="d-flex align-items-center justify-content-between gap-3">
                  <p className="ct_fs_16 mb-0 ct_darkblue_text">Profile Image</p>
                </div>
                <div className="d-flex align-items-center gap-3 ct_flex_col_575 mt-3">
                  <div className="ct_profile_pic ct_flex_shrink_0">
                    <img
                      src={values.profile_image_preview || defaultProfileImage}
                      alt="Profile"
                    />
                  </div>
                  <div className="ct_flex_1">
                    <label className="ct_upload_btn">
                      Upload Profile Photo
                      <input
                        className="ct_file_input"
                        type="file"
                        accept="image/*"
                        onChange={(event) => {
                          const file = event.currentTarget.files?.[0];
                          if (file) {
                            const previewUrl = URL.createObjectURL(file);
                            setFieldValue("profile_image", file);
                            setFieldValue("profile_image_preview", previewUrl);
                          }
                        }}
                      />
                    </label>
                    <p className="ct_fs_12 ct_grey_text mt-2 mb-0">
                      Upload a clear headshot for your profile.
                    </p>
                  </div>
                </div>
                <ErrorMessage
                  errors={errors}
                  touched={touched}
                  fieldName="profile_image"
                />
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 ct_mt_40">
                <div className="d-flex align-items-center justify-content-between gap-3 ct_mb_20">
                  <p className="ct_fs_16 mb-0 ct_darkblue_text">Personal Information</p>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Full Name
                      </label>
                      <input
                        className="form-control ct_input"
                        placeholder="Full Name"
                        type="text"
                        id="full_name"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          handleChange(e);
                          const name = e.target.value || "";
                          const parts = name.trim().split(/\s+/);
                          const first = parts[0] || "";
                          const last = parts.slice(1).join(" ");
                          handleChange({
                            target: { id: "first_name", value: first },
                          });
                          handleChange({
                            target: { id: "last_name", value: last },
                          });
                        }}
                        value={
                          values.full_name ||
                          `${values.first_name} ${values.last_name}`.trim()
                        }
                      />
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="full_name"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Email Address
                      </label>
                      <input
                        className="form-control ct_input"
                        placeholder="Email Address"
                        value={values.email}
                        type="email"
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Phone Number
                      </label>
                      <PhoneInput
                        id="phone_number"
                        className="ct_phone_input"
                        defaultCountry="MU"
                        value={normalizePhoneValue(values.phone_number)}
                        onChange={(val) => {
                          const safeVal = val ?? "";
                          setFieldValue("phone_number", safeVal);
                        }}
                        onBlur={() => {
                          if (values.phone_number && !isValidPhoneNumber(values.phone_number)) {
                            setFieldError("phone_number", "Please enter valid phone number");
                          }
                        }}
                      />
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="phone_number"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Country</label>
                      <input
                        className="form-control ct_input"
                        placeholder="Country"
                        type="text"
                        id="country"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.country}
                      />
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="country"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">City</label>
                      <input
                        className="form-control ct_input"
                        placeholder="City"
                        type="text"
                        id="city"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.city}
                      />
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="city"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        LinkedIn Profile
                      </label>
                      <input
                        className="form-control ct_input"
                        placeholder="LinkedIn Profile"
                        type="text"
                        id="linkedin_profile"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.linkedin_profile}
                      />
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="linkedin_profile"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Portfolio (Optional)
                      </label>
                      <input
                        className="form-control ct_input"
                        placeholder="Portfolio"
                        type="text"
                        id="portfolio_website"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.portfolio_website}
                      />
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="portfolio_website"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 ct_mt_40">
                <div className="d-flex align-items-center justify-content-between gap-3 ct_mb_20">
                  <p className="ct_fs_16 mb-0 ct_darkblue_text">About Me</p>
                </div>
                <textarea
                  className="form-control"
                  placeholder="Write a short bio"
                  rows={4}
                  id="bio"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.bio}
                />
                <ErrorMessage
                  errors={errors}
                  touched={touched}
                  fieldName="bio"
                />
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 ct_mt_40">
                <div className="d-flex align-items-center justify-content-between gap-3 ct_mb_20">
                  <p className="ct_fs_16 mb-0 ct_darkblue_text">Professional Information</p>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Primary Role / Profession
                      </label>
                      <div className="d-flex flex-column gap-2 position-relative">
                        <select
                          className="form-control ct_input"
                          id="primary_role"
                          onBlur={handleBlur}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            setFieldValue("primary_role", nextValue);
                            if (nextValue !== "Other") {
                              setFieldValue("other_primary_role", "");
                            }
                          }}
                          value={values?.primary_role}
                        >
                          <option value="">Select role</option>
                          {roleOptions.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                        <i className="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                      </div>
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName={"primary_role"}
                      />
                    </div>
                    {values?.primary_role === "Other" && (
                      <div className="form-group mb-4">
                        <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                          Enter Primary Role
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          id="other_primary_role"
                          name="other_primary_role"
                          placeholder="Enter role"
                          value={values?.other_primary_role}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <ErrorMessage
                          errors={errors}
                          touched={touched}
                          fieldName="other_primary_role"
                        />
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Years of Experience
                      </label>
                      <div className="d-flex flex-column gap-2 position-relative">
                        <select
                          className="form-control ct_input"
                          id="years_of_experience"
                          // onBlur={handleBlur}
                          // onChange={handleChange}
                          value={values.years_of_experience}
                          disabled
                        >
                          <option value="">Select</option>
                          <option value="0-2 years">0-2 years</option>
                          <option value="3-5 years">3-5 years</option>
                          <option value="6-10 years">6-10 years</option>
                          <option value="10+ years">10+ years</option>
                        </select>
                        <i class="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                      </div>
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="years_of_experience"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Skills
                      </label>
                      <TagInput
                        name="skills"
                        value={values.skills}
                        onChange={(name, val) => setFieldValue(name, val)}
                        placeholder="Add skills"
                      />
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="skills"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Tools & Software Used
                      </label>
                      <TagInput
                        name="tools"
                        value={values.tools}
                        onChange={(name, val) => setFieldValue(name, val)}
                        placeholder="Add tools"
                      />
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="tools"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Hourly Rate (Optional)
                      </label>
                      <input
                        className="form-control ct_input"
                        placeholder="Hourly Rate"
                        type="text"
                        id="hourly_rate"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.hourly_rate}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Availability
                      </label>
                      <div className="d-flex flex-column gap-2">
                        {availabilityOptions.map((option) => (
                          <label key={option.value} className="ct_checkbox_label d-flex gap-2">
                            <input
                              type="checkbox"
                              checked={hasOption(values.availability, option.value)}
                              onChange={() =>
                                toggleAvailability(
                                  values.availability,
                                  option.value,
                                  setFieldValue,
                                  setFieldError,
                                  setFieldTouched
                                )
                              }
                            />
                            <span className="ct_fs_14 ct_darkblue_text">
                              {option.label}
                            </span>
                          </label>
                        ))}
                      </div>
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="availability"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Preferred Project Type
                      </label>
                      <div className="d-flex flex-column gap-2">
                        {projectTypeOptions.map((option) => (
                          <label key={option} className="ct_checkbox_label d-flex gap-2">
                            <input
                              type="checkbox"
                              checked={hasOption(values.preferred_project_type, option)}
                              onChange={(event) => {
                                if (event.target.checked) {
                                  setFieldValue("preferred_project_type", [
                                    ...values.preferred_project_type,
                                    option,
                                  ]);
                                } else {
                                  setFieldValue(
                                    "preferred_project_type",
                                    values.preferred_project_type.filter((item) => item !== option)
                                  );
                                }
                              }}
                            />
                            <span className="ct_fs_14 ct_darkblue_text">{option}</span>
                          </label>
                        ))}
                      </div>
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="preferred_project_type"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 ct_mt_40">
                <div className="d-flex align-items-center justify-content-between gap-3 ct_mb_20">
                  <p className="ct_fs_16 mb-0 ct_darkblue_text">Experience</p>
                </div>
                <FieldArray
                  name="experiences"
                  render={(arrayHelpers) => (
                    <div className="d-flex flex-column gap-4">
                      {values.experiences.map((item, index) => (
                        <div key={index} className="ct_outline_bg ct_border_radius_10 ct_p_20">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <p className="ct_fs_14 ct_darkblue_text mb-0">Experience {index + 1}</p>
                            {values.experiences.length > 1 && (
                              <button
                                type="button"
                                className="ct_outline_btn ct_h_32"
                                onClick={() => {
                                  const expId =
                                    item?.experience_id || item?.id || item?.experienceId;
                                  if (expId) {
                                    setFieldValue("delete_experience_ids", [
                                      ...(values.delete_experience_ids || []),
                                      expId,
                                    ]);
                                  }
                                  arrayHelpers.remove(index);
                                }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="form-group mb-3">
                                <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                  Job Title
                                </label>
                                <input
                                  className="form-control ct_input"
                                  placeholder="Job Title"
                                  type="text"
                                  value={item.job_title}
                                  onChange={(event) =>
                                    setFieldValue(
                                      `experiences.${index}.job_title`,
                                      event.target.value
                                    )
                                  }
                                />
                                <ErrorMessage
                                  errors={errors}
                                  touched={touched}
                                  fieldName={`experiences.${index}.job_title`}
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group mb-3">
                                <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                  Company Name
                                </label>
                                <input
                                  className="form-control ct_input"
                                  placeholder="Company Name"
                                  type="text"
                                  value={item.company_name}
                                  onChange={(event) =>
                                    setFieldValue(
                                      `experiences.${index}.company_name`,
                                      event.target.value
                                    )
                                  }
                                />
                                <ErrorMessage
                                  errors={errors}
                                  touched={touched}
                                  fieldName={`experiences.${index}.company_name`}
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group mb-3">
                                <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                  Location
                                </label>
                                <input
                                  className="form-control ct_input"
                                  placeholder="Location"
                                  type="text"
                                  value={item.location}
                                  onChange={(event) =>
                                    setFieldValue(
                                      `experiences.${index}.location`,
                                      event.target.value
                                    )
                                  }
                                />
                                <ErrorMessage
                                  errors={errors}
                                  touched={touched}
                                  fieldName={`experiences.${index}.location`}
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group mb-3">
                                <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                  Start Date
                                </label>
                                <input
                                  className="form-control ct_input"
                                  type="date"
                                  max={new Date().toISOString().split("T")[0]}
                                  value={item.start_date}
                                  onChange={(event) =>
                                    (() => {
                                      const nextStartDate = event.target.value;
                                      setFieldValue(
                                        `experiences.${index}.start_date`,
                                        nextStartDate
                                      );
                                      if (
                                        item.end_date &&
                                        nextStartDate &&
                                        item.end_date < nextStartDate
                                      ) {
                                        setFieldValue(
                                          `experiences.${index}.end_date`,
                                          nextStartDate
                                        );
                                      }
                                    })()
                                  }
                                />
                                <ErrorMessage
                                  errors={errors}
                                  touched={touched}
                                  fieldName={`experiences.${index}.start_date`}
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group mb-3">
                                <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                  End Date
                                </label>
                                <input
                                  className="form-control ct_input"
                                  type="date"
                                  max={new Date().toISOString().split("T")[0]}
                                  min={item.start_date || ""}
                                  value={item.end_date}
                                  onChange={(event) =>
                                    (() => {
                                      const nextEndDate = event.target.value;
                                      if (
                                        item.start_date &&
                                        nextEndDate &&
                                        nextEndDate < item.start_date
                                      ) {
                                        setFieldValue(
                                          `experiences.${index}.end_date`,
                                          item.start_date
                                        );
                                      } else {
                                        setFieldValue(
                                          `experiences.${index}.end_date`,
                                          nextEndDate
                                        );
                                      }
                                    })()
                                  }
                                  disabled={item.currently_working}
                                />
                                <ErrorMessage
                                  errors={errors}
                                  touched={touched}
                                  fieldName={`experiences.${index}.end_date`}
                                />
                              </div>
                            </div>
                            <div className="col-md-12">
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  id={`experience_current_${index}`}
                                  checked={item.currently_working}
                                  onChange={(event) =>
                                    setFieldValue(
                                      `experiences.${index}.currently_working`,
                                      event.target.checked
                                    )
                                  }
                                />
                                <label
                                  htmlFor={`experience_current_${index}`}
                                  className="ct_fs_14 ct_darkblue_text mb-0"
                                >
                                  Currently working here
                                </label>
                              </div>
                            </div>
                            <div className="col-md-12">
                              <div className="form-group mb-3">
                                <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                  Work Experience
                                </label>
                                <textarea
                                  className="form-control ct_input h-auto"
                                  rows={4}
                                  value={item.job_description}
                                  onChange={(event) =>
                                    setFieldValue(
                                      `experiences.${index}.job_description`,
                                      event.target.value
                                    )
                                  }
                                />
                                <ErrorMessage
                                  errors={errors}
                                  touched={touched}
                                  fieldName={`experiences.${index}.job_description`}
                                />
                              </div>
                            </div>
                            <div className="col-md-12">
                              <div className="form-group mb-3">
                                <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                  Company Image
                                </label>
                                <PdfOrImageUploader
                                  maxFiles={1}
                                  multiple={false}
                                  acceptType="image/*"
                                  placeholder="Upload Company Image"
                                  replaceOnSelect={true}
                                  existingFiles={
                                    item?.experience_images
                                      ? [
                                        (() => {
                                          const rawValue =
                                            item.experience_images ||
                                            item.image ||
                                            item.experience_image ||
                                            "";
                                          const raw = Array.isArray(rawValue)
                                            ? rawValue[0]
                                            : rawValue;
                                          if (!raw) return null;
                                          if (raw instanceof File) {
                                            return {
                                              id: raw.name || index,
                                              file: raw,
                                              name: raw.name || `experience-${index + 1}`,
                                              type: raw.type || "image",
                                              preview: URL.createObjectURL(raw),
                                              isExisting: false,
                                            };
                                          }
                                          if (raw?.file instanceof File) {
                                            return {
                                              id: raw.id || index,
                                              file: raw.file,
                                              name: raw.name || raw.file.name || `experience-${index + 1}`,
                                              type: raw.type || raw.file.type || "image",
                                              preview:
                                                raw.preview || URL.createObjectURL(raw.file),
                                              isExisting: false,
                                            };
                                          }
                                          const url =
                                            (typeof raw === "string" && raw) ||
                                            raw?.url ||
                                            raw?.preview ||
                                            (typeof raw?.file === "string" ? raw.file : "");
                                          if (!url) return null;
                                          return {
                                            id: raw?.id || index,
                                            file: url,
                                            name:
                                              raw?.name ||
                                              (typeof url === "string"
                                                ? url.split("/").pop()
                                                : `experience-${index + 1}`),
                                            type: raw?.type || "image",
                                            preview: url,
                                            isExisting: true,
                                          };
                                        })(),
                                      ].filter(Boolean)
                                      : []
                                  }
                                  onChange={(newFiles) => {
                                    setFieldValue(
                                      `experiences.${index}.experience_images`,
                                      newFiles?.[0] || ""
                                    );
                                  }}
                                />
                                <ErrorMessage
                                  errors={errors}
                                  touched={touched}
                                  fieldName={`experiences.${index}.experience_images`}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="ct_yellow_btn ct_outline_grey ct_h_44 align-self-start"
                        onClick={() =>
                          arrayHelpers.push({
                            job_title: "",
                            company_name: "",
                            job_description: "",
                            location: "",
                            experience_images: "",
                            start_date: "",
                            end_date: "",
                            currently_working: false,
                            link: "",
                          })
                        }
                      >
                        Add Experience
                      </button>
                    </div>
                  )}
                />
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 ct_mt_40">
                <div className="d-flex align-items-center justify-content-between gap-3 ct_mb_20">
                  <p className="ct_fs_16 mb-0 ct_darkblue_text">Work Samples</p>
                </div>
                <FieldArray
                  name="portfolio_links"
                  render={(arrayHelpers) => (
                    <div className="d-flex flex-column gap-3">
                      {values.portfolio_links.map((link, index) => (
                        <div key={index} className="d-flex flex-column gap-2 ct_work_sample_row">
                          <div className="d-flex gap-2 align-items-center">
                            <input
                              className="form-control ct_input"
                              placeholder="https://"
                              type="text"
                              name={`portfolio_links.${index}`}
                              value={link}
                              onChange={(event) =>
                                setFieldValue(`portfolio_links.${index}`, event.target.value)
                              }
                              onBlur={handleBlur}
                            />
                            {values.portfolio_links.length > 1 && (
                              <button
                                type="button"
                                className="ct_outline_btn ct_h_32 ct_btn_inline"
                                onClick={() => arrayHelpers.remove(index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          {(() => {
                            const linkErrors = errors?.portfolio_links;
                            const linkTouched = touched?.portfolio_links;
                            const errorForIndex =
                              Array.isArray(linkErrors) ? linkErrors[index] : null;
                            const touchedForIndex =
                              Array.isArray(linkTouched) ? linkTouched[index] : null;
                            if (typeof errorForIndex === "string" && touchedForIndex) {
                              return <span style={{ color: "red" }}>{errorForIndex}</span>;
                            }
                            return null;
                          })()}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="ct_yellow_btn ct_outline_grey ct_h_44 align-self-start"
                        onClick={() => arrayHelpers.push("")}
                      >
                        Add Link
                      </button>
                    </div>
                  )}
                />
                {(() => {
                  const listErrors = errors?.portfolio_links;
                  const listTouched = touched?.portfolio_links;
                  const hasIndexError =
                    Array.isArray(listErrors) && listErrors.some(Boolean);
                  const hasTouched =
                    Array.isArray(listTouched)
                      ? listTouched.some(Boolean)
                      : !!listTouched;
                  if (typeof listErrors === "string" && hasTouched && !hasIndexError) {
                    return <span style={{ color: "red" }}>{listErrors}</span>;
                  }
                  return null;
                })()}

                <div className="mt-4">
                  <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                    Upload CV (PDF only)
                  </label>
                  <PdfOrImageUploader
                    maxFiles={1}
                    multiple={false}
                    acceptType="application/pdf,.pdf"
                    placeholder="Upload CV (PDF only)"
                    replaceOnSelect={true}
                    existingFiles={
                      values?.cv && Array.isArray(values.cv)
                        ? values.cv.map((file, idx) => ({
                          id: file?.id || idx,
                          file: file,
                          name:
                            file?.name ||
                            (typeof file === "string"
                              ? file.split("/").pop()
                              : `cv-${idx + 1}.pdf`),
                          type: "pdf",
                          preview:
                            typeof file === "string"
                              ? file
                              : file?.preview || file?.url || "",
                          isExisting: !(file instanceof File),
                        }))
                        : []
                    }
                    onChange={(newFiles) => {
                      setFieldValue("cv", newFiles);
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    touched={touched}
                    fieldName="cv"
                  />
                </div>

                <div className="mt-4">
                  <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                    Upload Certificates (PDF or Image)
                  </label>
                  <PdfOrImageUploader
                    maxFiles={5}
                    multiple={true}
                    acceptType="application/pdf,image/*"
                    placeholder="Upload Certificates (PDF or Image)"
                    existingFiles={
                      values?.certificates && Array.isArray(values.certificates)
                        ? values.certificates.map((file, idx) => ({
                          id: file?.id || file?.certificate_id,
                          file: file,
                          name:
                            file?.name ||
                            (typeof file === "string"
                              ? file.split("/").pop()
                              : `certificate-${idx + 1}`),
                          type:
                            typeof file === "string"
                              ? file.toLowerCase().includes(".pdf")
                                ? "pdf"
                                : "image"
                              : file?.type
                                ? file.type
                                : (file?.url || file?.preview || file?.file || "")
                                  .toLowerCase()
                                  .includes(".pdf")
                                  ? "pdf"
                                  : "image",
                          preview:
                            typeof file === "string"
                              ? file
                              : file?.preview || file?.url || file?.file || "",
                          isExisting: !(file instanceof File),
                        }))
                        : []
                    }
                    onChange={(newFiles, removedIds = []) => {
                      setFieldValue("certificates", newFiles);
                      setFieldValue("delete_certificate_ids", removedIds);
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    touched={touched}
                    fieldName="certificates"
                  />
                </div>

                <div className="mt-4">
                  <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                    Upload Work Samples (Optional)
                  </label>
                  <label className="ct_upload_btn">
                    Upload Work Samples
                    <input
                      className="ct_file_input"
                      type="file"
                      multiple
                      accept=".zip,application/zip,application/x-zip-compressed"
                      onChange={(event) => {
                        const files = Array.from(event.currentTarget.files || []);
                        const zipFiles = files.filter((file) => {
                          const lowerName = (file.name || "").toLowerCase();
                          return (
                            lowerName.endsWith(".zip") ||
                            file.type === "application/zip" ||
                            file.type === "application/x-zip-compressed"
                          );
                        });
                        if (zipFiles.length) {
                          setFieldValue("work_samples", [...values.work_samples, ...zipFiles]);
                        }
                        event.currentTarget.value = "";
                      }}
                    />
                  </label>
                  {values.work_samples?.length > 0 && (
                    <div className="mt-3 ct_work_sample_list">
                      {(() => {
                        const entries = values.work_samples.map((item, index) => ({
                          item,
                          index,
                        }));
                        const isNewFile = (entry) => entry.item instanceof File;
                        const orderedEntries = [
                          ...entries.filter(isNewFile),
                          ...entries.filter((entry) => !isNewFile(entry)),
                        ];
                        return orderedEntries;
                      })().map((entry) => {
                        const { item, index } = entry;
                        const label =
                          item instanceof File
                            ? item.name
                            : item?.name ||
                            item?.project_url?.split("/").pop() ||
                            item?.url?.split("/").pop() ||
                            `Sample ${index + 1}`;
                        return (
                          <div
                            key={`${label}-${index}`}
                            className="ct_work_sample_item"
                          >
                            <span className="ct_badge_grey ct_work_sample_label">
                              {label}
                            </span>
                            <button
                              type="button"
                              className="ct_outline_btn ct_h_32 ct_btn_inline"
                              onClick={() => {
                                const removedItem = values.work_samples[index];
                                if (removedItem?.id) {
                                  setFieldValue("delete_project_ids", [
                                    ...(values.delete_project_ids || []),
                                    removedItem.id,
                                  ]);
                                }
                                setFieldValue(
                                  "work_samples",
                                  values.work_samples.filter((_, i) => i !== index)
                                );
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {/* <div className="ct_outline_bg ct_border_radius_10 ct_p_20 ct_mt_40">
                <div className="d-flex align-items-center justify-content-between gap-3 ct_mb_20">
                  <p className="ct_fs_16 mb-0 ct_darkblue_text">NUVIO Network</p>
                </div>
                <div className="form-group mb-4">
                  <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                    Why do you want to join NUVIO?
                  </label>
                  <textarea
                    className="form-control ct_input"
                    rows={3}
                    id="join_reason"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.join_reason}
                  />
                </div>
                <div className="form-group mb-4">
                  <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                    What type of clients are you interested in working with?
                  </label>
                  <textarea
                    className="form-control ct_input"
                    rows={3}
                    id="interested_clients"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.interested_clients}
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                    How did you hear about NUVIO?
                  </label>
                  <select
                    className="form-control ct_input"
                    id="heard_about"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.heard_about}
                  >
                    <option value="">Select</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="referral">Referral</option>
                    <option value="website">Website</option>
                    <option value="event">Event / Webinar</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div> */}
              <div className="d-flex justify-content-end gap-3 ct_mt_40">
                <button
                  type="button"
                  className="ct_outline_btn ct_h_44"
                  onClick={() => navigate(findAJobPaths.Profile)}
                >
                  Cancel
                </button>
                <button type="submit" className="ct_yellow_btn ct_h_44">
                  Save Changes
                </button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </FreelancerLayout>
  );
};

export default EditProfile;
